import { z } from "zod";

import {
  ALLERGY_OPTIONS,
  DIET_OPTIONS,
  EXERCISE_OPTIONS,
  FAMILY_HISTORY_CONDITIONS,
  FAMILY_HISTORY_RELATIONS,
  LIFESTYLE_FACTOR_OPTIONS,
  MEDICAL_CONDITION_OPTIONS,
  OUTCOME_OPTIONS,
  SCREENING_OPTIONS,
  SLEEP_HABIT_OPTIONS,
  SYMPTOM_OPTIONS,
} from "@/lib/questionnaire/options";

function isValidUsE164(value: string) {
  return /^\+1\d{10}$/.test(value);
}

function isAtLeast18(dateOfBirth: Date, now = new Date()) {
  const eighteenthBirthday = new Date(dateOfBirth);
  eighteenthBirthday.setFullYear(eighteenthBirthday.getFullYear() + 18);
  return eighteenthBirthday <= now;
}

function enumFromOptions<const T extends readonly { id: string }[]>(
  options: T,
) {
  type Id = T[number]["id"];
  return z.enum(options.map((option) => option.id) as [Id, ...Id[]]);
}

function objectFromOptions<
  const T extends readonly { id: string }[],
  S extends z.ZodType,
>(options: T, valueSchema: S) {
  type Id = T[number]["id"];
  const shape = Object.fromEntries(
    options.map((option) => [option.id, valueSchema]),
  ) as { [K in Id]: S };
  return z.object(shape);
}

const symptomIdSchema = enumFromOptions(SYMPTOM_OPTIONS);
const outcomeIdSchema = enumFromOptions(OUTCOME_OPTIONS);
const allergyIdSchema = enumFromOptions(ALLERGY_OPTIONS);
const medicalConditionIdSchema = enumFromOptions(MEDICAL_CONDITION_OPTIONS);
const dietIdSchema = enumFromOptions(DIET_OPTIONS);
const exerciseIdSchema = enumFromOptions(EXERCISE_OPTIONS);
const sleepHabitIdSchema = enumFromOptions(SLEEP_HABIT_OPTIONS);
const lifestyleFactorIdSchema = enumFromOptions(LIFESTYLE_FACTOR_OPTIONS);
const screeningIdSchema = enumFromOptions(SCREENING_OPTIONS);

export const basicInfoSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  dateOfBirth: z
    .date({ error: "Date of birth is required" })
    .max(new Date(), {
      error: "Date of birth cannot be in the future",
    })
    .refine((value) => isAtLeast18(value), {
      error: "You must be 18 or older to continue",
    }),
  email: z.email({ error: "Enter a valid email" }),
  phone: z
    .string()
    .trim()
    .min(1, "Phone is required")
    .refine((value) => isValidUsE164(value), {
      error: "Enter a valid US phone number",
    }),
  privacyAgreed: z.literal(true, {
    error: "Agree to the privacy policy to continue",
  }),
});

export const symptomsSchema = z
  .object({
    selectedSymptoms: z.array(symptomIdSchema),
    additionalSymptoms: z.string(),
  })
  .refine(
    (value) =>
      value.selectedSymptoms.length > 0 ||
      value.additionalSymptoms.trim().length > 0,
    {
      error: "Select at least one symptom or describe additional symptoms",
      path: ["selectedSymptoms"],
    },
  );

export const outcomesSchema = z
  .object({
    selectedOutcomes: z.array(outcomeIdSchema),
    additionalOutcomes: z.string(),
  })
  .refine(
    (value) =>
      value.selectedOutcomes.length > 0 ||
      value.additionalOutcomes.trim().length > 0,
    {
      error: "Select at least one outcome or describe additional outcomes",
      path: ["selectedOutcomes"],
    },
  );

const familyHistoryRelationFlagsSchema = objectFromOptions(
  FAMILY_HISTORY_RELATIONS,
  z.boolean(),
);

const familyMedicalHistorySchema = objectFromOptions(
  FAMILY_HISTORY_CONDITIONS,
  familyHistoryRelationFlagsSchema,
);

export type FamilyMedicalHistory = z.infer<typeof familyMedicalHistorySchema>;

const allergiesSchema = z
  .object({
    selectedAllergies: z.array(allergyIdSchema),
    additionalAllergies: z.string(),
  })
  .refine(
    (value) =>
      value.selectedAllergies.length > 0 ||
      value.additionalAllergies.trim().length > 0,
    {
      error: "Select at least one allergy or describe additional allergies",
      path: ["selectedAllergies"],
    },
  );

const medicalConditionsSchema = z
  .object({
    selectedMedicalConditions: z.array(medicalConditionIdSchema),
    additionalMedicalConditions: z.string(),
  })
  .refine(
    (value) =>
      value.selectedMedicalConditions.length > 0 ||
      value.additionalMedicalConditions.trim().length > 0,
    {
      error: "Select at least one condition or describe additional conditions",
      path: ["selectedMedicalConditions"],
    },
  );

export const medicalHistorySchema = allergiesSchema
  .and(medicalConditionsSchema)
  .and(
    z.object({
      familyMedicalHistory: familyMedicalHistorySchema,
    }),
  );

export const lifestyleSchema = z
  .object({
    currentWeight: z.coerce
      .number({ error: "Current weight is required" })
      .positive("Enter a valid weight"),
    heightFeet: z.coerce
      .number({ error: "Height is required" })
      .int("Enter height in whole feet")
      .min(3, "Enter a valid height")
      .max(8, "Enter a valid height"),
    heightInches: z.coerce
      .number({ error: "Height is required" })
      .int("Enter inches as a whole number")
      .min(0, "Inches must be 0–11")
      .max(11, "Inches must be 0–11"),
    selectedDiets: z.array(dietIdSchema),
    additionalDiet: z.string(),
    selectedExercise: z.array(exerciseIdSchema),
    additionalExercise: z.string(),
    selectedSleepHabits: z.array(sleepHabitIdSchema),
    additionalSleepHabits: z.string(),
    selectedLifestyleFactors: z.array(lifestyleFactorIdSchema),
    additionalLifestyleFactors: z.string(),
  })
  .refine(
    (value) =>
      value.selectedDiets.length > 0 || value.additionalDiet.trim().length > 0,
    {
      error: "Select at least one diet option or describe your diet",
      path: ["selectedDiets"],
    },
  )
  .refine(
    (value) =>
      value.selectedExercise.length > 0 ||
      value.additionalExercise.trim().length > 0,
    {
      error: "Select at least one exercise option or describe your exercise",
      path: ["selectedExercise"],
    },
  )
  .refine(
    (value) =>
      value.selectedSleepHabits.length > 0 ||
      value.additionalSleepHabits.trim().length > 0,
    {
      error: "Select at least one sleep habit or describe your sleep",
      path: ["selectedSleepHabits"],
    },
  )
  .refine(
    (value) =>
      value.selectedLifestyleFactors.length > 0 ||
      value.additionalLifestyleFactors.trim().length > 0,
    {
      error:
        "Select at least one lifestyle factor or describe additional factors",
      path: ["selectedLifestyleFactors"],
    },
  );

const optionalItemListSchema = z.array(z.string().trim().min(1));

export const medicationEntrySchema = z.object({
  name: z.string().trim().min(1),
  timesPerDay: z.number().int().min(1).max(24),
});

export type MedicationEntry = z.infer<typeof medicationEntrySchema>;

export function formatTimesPerDay(timesPerDay: number) {
  return timesPerDay === 1 ? "1 time per day" : `${timesPerDay} times per day`;
}

export function formatMedicationEntry(entry: MedicationEntry) {
  return `${entry.name} — ${formatTimesPerDay(entry.timesPerDay)}`;
}

export const medicationsSchema = z.object({
  currentSupplements: optionalItemListSchema,
  currentMedications: z.array(medicationEntrySchema),
});

export const screeningSchema = z
  .object({
    selectedScreening: z.array(screeningIdSchema),
    additionalScreening: z.string(),
  })
  .refine(
    (value) =>
      value.selectedScreening.length > 0 ||
      value.additionalScreening.trim().length > 0,
    {
      error:
        "Select at least one option or describe additional screening details",
      path: ["selectedScreening"],
    },
  );

export const questionnaireSchema = basicInfoSchema
  .and(symptomsSchema)
  .and(outcomesSchema)
  .and(medicalHistorySchema)
  .and(lifestyleSchema)
  .and(medicationsSchema)
  .and(screeningSchema);

export type QuestionnaireValues = z.infer<typeof questionnaireSchema>;
